import { createThunk, model, Model } from 'mst-collection';
import { types, IAnyModelType, getRoot } from 'mobx-state-tree';

import { Api, UserFollowActionEnum, FriendshipTypeEnum } from '@api';
import { UserSettings, UserSettingsModel } from './UserSettingsModel';
import { PhotoModel } from '../common/PhotoModel';
import { UserInvite, UserInviteModel } from './UserInviteModel';
import { ThunkAbortController } from '../utils';
import {
  FriendshipUserList,
  FriendshipUserListModel,
} from './FriendshipUserListModel';
import {
  UserSharedData,
  UserSharedDataModel,
} from './UserSharedDataModel';
import {
  UserTariffPlan,
  UserTariffPlanModel,
} from './UserTariffPlanModel';
import {
  UserPostList,
  UserPostListModel,
} from '../Posts/UserPostListModel';
import type { RootModelType } from '../RootModel';

/**
 * Some fields are optional for User but not for CurrentUser (in Viewer)
 */
export interface ICurrentUser extends User {
  email: string;
  phone: string;
  invite: UserInvite;
  settings: UserSettings;
  tariffPlan: UserTariffPlan;
  followers: FriendshipUserList;
  following: FriendshipUserList;
  posts: UserPostList;
  shared: UserSharedData;
}

export interface IUser extends User {
  followers: FriendshipUserList;
  following: FriendshipUserList;
  posts: UserPostList;
}

export class User extends Model({
  id: types.identifierNumber,
  email: types.maybe(types.string),
  firstName: types.string,
  lastName: types.string,
  photo: PhotoModel,
  phone: types.maybe(types.string),
  location: types.string,
  description: types.string,
  invite: types.maybe(UserInviteModel),
  settings: types.maybe(UserSettingsModel),
  shared: types.maybe(UserSharedDataModel),
  tariffPlan: types.maybe(UserTariffPlanModel),

  isFollowing: false,

  // We are casting FriendshipUserListModel to IAnyModelType because we get issues with cycle type casting User => FriendshipUserList => User
  followers: types.optional(
    types.late((): IAnyModelType => FriendshipUserListModel),
    {
      friendshipType: FriendshipTypeEnum.Followers,
    },
  ),
  following: types.optional(
    types.late((): IAnyModelType => FriendshipUserListModel),
    {
      friendshipType: FriendshipTypeEnum.Following,
    },
  ),

  // We are casting UserPostListModel to IAnyModelType because we get issues with cycle type casting User => UserPostList => PostList => User
  posts: types.optional(
    types.late((): IAnyModelType => UserPostListModel),
    {},
  ),
}) {
  get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  get truncatedFullName() {
    return this.fullName.length > 30
      ? `${this.fullName.slice(0, 30)}...`
      : this.fullName;
  }

  get hasPhoto() {
    // Photo can be empty object, if profile photo is not uploaded yet
    return !!this.photo.big;
  }

  get isViewer(): boolean {
    return (
      getRoot<RootModelType>(this).viewer.currentUser?.id === this.id
    );
  }

  toggleIsFollowing() {
    this.isFollowing = !this.isFollowing;
  }

  private _toggleFollowAbortController = new ThunkAbortController();

  toggleFollow = createThunk(() => {
    return async function toggleFollow(this: User) {
      try {
        // Cancel previous request if it in progress
        this._toggleFollowAbortController.abort();

        this.toggleIsFollowing();

        const action = this.isFollowing
          ? UserFollowActionEnum.Follow
          : UserFollowActionEnum.Unfollow;

        await Api.Friendships.toggleFollow(
          {
            id: this.id,
            action,
          },
          this._toggleFollowAbortController.create(),
        );

        this._toggleFollowAbortController.remove();
      } catch (error) {
        // If request was canceled do nothing
        if (ThunkAbortController.isCanceled(error)) {
          return;
        }
        // Change follow status back
        this.toggleIsFollowing();

        throw error;
      }
    };
  });
}

export const UserModel = model(User);
